import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';
import { collection, addDoc, getDocs } from 'firebase/firestore';
import { LuEraser, LuInfo, LuLoaderCircle, LuReceipt, LuSave } from 'react-icons/lu';
import { db } from '../../../config/firebaseConfig';
import { Field, SelectInput, TextArea, TextInput } from '../../../components/form/FormControls';
import Button from '../../../components/ui/Button';
import GlassIcon from '../../../components/ui/GlassIcon';
import Modal from '../../../components/ui/Modal';
import useEmployeeFinanceText from './employeeFinanceText';

const FORM_ID = 'payment-form';
// Kayıtlı değerler (Türkçe kalır)
const PAYMENT_TYPES = ['Avans', 'Prim', 'Harcırah', 'Yol-Yemek', 'Diğer'];
const EMPTY_PAYMENT = { paymentType: '', paymentDate: '', paidAmount: '', description: '', projectId: '' };

/**
 * New payment for an employee (Payments): project (only the employee's
 * projects), type, date, amount and reason. Same record as before.
 */
const AddPaymentModal = ({ onClose, onPaymentAdded, employeeId, employeeName }) => {
  const { language, label } = useEmployeeFinanceText();
  const [paymentData, setPaymentData] = useState(EMPTY_PAYMENT);
  const [projects, setProjects] = useState([]);
  const [projectsLoaded, setProjectsLoaded] = useState(false);
  const [saving, setSaving] = useState(false);

  const handleInputChange = (name, value) => setPaymentData((prev) => ({ ...prev, [name]: value }));

  useEffect(() => {
    const fetchEmployeeProjects = async () => {
      try {
        const querySnapshot = await getDocs(collection(db, 'Projects'));
        const projectsList = querySnapshot.docs
          .map((docSnapshot) => ({ id: docSnapshot.id, ...docSnapshot.data() }))
          .filter((project) => project.employees?.some((emp) => emp.id === employeeId));
        setProjects(projectsList);
      } catch (error) {
        console.error('Error fetching projects:', error);
        toast.error(label('paymentForm.projectsError'));
      } finally {
        setProjectsLoaded(true);
      }
    };
    if (employeeId) fetchEmployeeProjects();
  }, [employeeId, label]);

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!paymentData.paymentType || !paymentData.paymentDate || !paymentData.paidAmount || !paymentData.projectId) {
      toast.error(label('paymentForm.required'));
      return;
    }

    setSaving(true);
    try {
      await addDoc(collection(db, 'Payments'), {
        employeeId,
        projectId: paymentData.projectId,
        paymentType: paymentData.paymentType,
        paymentDate: new Date(paymentData.paymentDate).toISOString(),
        paidAmount: Number(paymentData.paidAmount),
        description: paymentData.description,
        status: 'Ödendi',
        createdAt: new Date().toISOString(),
      });

      toast.success(label('paymentForm.added'));
      onPaymentAdded();
      onClose();
    } catch (error) {
      console.error('Ödeme eklenirken hata:', error);
      toast.error(label('paymentForm.error'));
    } finally {
      setSaving(false);
    }
  };

  const noProjects = projectsLoaded && projects.length === 0;

  return (
    <Modal
      isOpen
      onClose={onClose}
      size="md"
      dismissible={!saving}
      title={label('paymentForm.title')}
      subtitle={employeeName}
      closeLabel={label('common.cancel')}
      lang={language}
      icon={(
        <GlassIcon gradient="tw-from-sky-400 tw-to-blue-600 tw-shadow-blue-500/30" size="xs">
          <LuReceipt aria-hidden="true" className="tw-h-[18px] tw-w-[18px]" />
        </GlassIcon>
      )}
      footer={(
        <>
          <Button variant="ghost" onClick={onClose} disabled={saving}>{label('common.cancel')}</Button>
          <Button variant="secondary" onClick={() => setPaymentData(EMPTY_PAYMENT)} disabled={saving}>
            <LuEraser aria-hidden="true" className="tw-h-4 tw-w-4" />
            {label('common.clear')}
          </Button>
          <Button variant="primary" type="submit" form={FORM_ID} disabled={saving || projects.length === 0}>
            {saving ? <LuLoaderCircle aria-hidden="true" className="tw-h-4 tw-w-4 tw-animate-spin" /> : <LuSave aria-hidden="true" className="tw-h-4 tw-w-4" />}
            {label('paymentForm.submit')}
          </Button>
        </>
      )}
    >
      <form id={FORM_ID} onSubmit={handleSubmit} className="tw-space-y-4">
        {noProjects && (
          <p className="tw-flex tw-items-start tw-gap-2 tw-rounded-2xl tw-border tw-border-amber-500/25 tw-bg-amber-500/[0.08] tw-px-3.5 tw-py-3 tw-text-sm tw-text-amber-800 dark:tw-text-amber-200">
            <LuInfo aria-hidden="true" className="tw-mt-0.5 tw-h-4 tw-w-4 tw-shrink-0" />
            {label('paymentForm.noProjects')}
          </p>
        )}

        <Field label={label('paymentForm.project')} htmlFor="payment-project" required>
          <SelectInput
            id="payment-project"
            value={paymentData.projectId}
            onChange={(event) => handleInputChange('projectId', event.target.value)}
            placeholder={label('paymentForm.projectPlaceholder')}
            options={projects.map((project) => ({ value: project.id, label: project.titleShort || project.id }))}
            required
          />
        </Field>

        <div className="tw-grid tw-gap-4 sm:tw-grid-cols-2">
          <Field label={label('paymentForm.type')} htmlFor="payment-type" required>
            <SelectInput
              id="payment-type"
              value={paymentData.paymentType}
              onChange={(event) => handleInputChange('paymentType', event.target.value)}
              placeholder={label('common.choose')}
              options={PAYMENT_TYPES.map((type) => ({ value: type, label: label(`paymentTypes.${type}`) }))}
              required
            />
          </Field>
          <Field label={label('paymentForm.date')} htmlFor="payment-date" required>
            <TextInput id="payment-date" type="date" value={paymentData.paymentDate} onChange={(event) => handleInputChange('paymentDate', event.target.value)} required />
          </Field>
        </div>

        <Field label={label('paymentForm.amount')} htmlFor="payment-amount" required>
          <TextInput
            id="payment-amount"
            type="number"
            inputMode="decimal"
            min="0"
            step="0.01"
            value={paymentData.paidAmount}
            onChange={(event) => handleInputChange('paidAmount', event.target.value)}
            placeholder="0.00"
            required
            className="tw-font-semibold tw-tabular-nums"
          />
        </Field>

        <Field label={label('paymentForm.reason')} htmlFor="payment-reason">
          <TextArea
            id="payment-reason"
            rows={3}
            value={paymentData.description}
            onChange={(event) => handleInputChange('description', event.target.value)}
            placeholder={label('paymentForm.reasonPlaceholder')}
          />
        </Field>
      </form>
    </Modal>
  );
};

AddPaymentModal.propTypes = {
  onClose: PropTypes.func.isRequired,
  onPaymentAdded: PropTypes.func.isRequired,
  employeeId: PropTypes.string.isRequired,
  employeeName: PropTypes.string,
};

export default AddPaymentModal;
